import type { MeMembership } from './team.types';

/**
 * Effective primitive permission for the resolved team, as returned by
 * `GET /me → membership.permissions`. The union comes from the generated API
 * client so an unknown permission string fails typecheck.
 */
export type TeamPermission = MeMembership['permissions'][number];

/**
 * What a gate falls back to while the selected team uses legacy roles, as
 * VRI client teams do. Each gate must declare the behavior of the code it replaces:
 *
 * - `allow`: the surface was visible to every member.
 * - `teamAdmin`: the surface was gated on the legacy team-admin role.
 * - `teamUser`: the surface was gated on the legacy non-admin member role.
 * - `vrpAdmin`: the surface was gated on the legacy platform-admin role.
 */
export type LegacyGate = 'allow' | 'teamAdmin' | 'teamUser' | 'vrpAdmin';

export type ResolveTeamPermissionInput = {
  permission: TeamPermission;
  permissions: readonly TeamPermission[];
  isUiPermissionRolesEnabled: boolean;
  legacy: LegacyGate;
  isTeamAdmin: boolean;
  isTeamUser: boolean;
  isVrpAdmin: boolean;
};

/** The API strips these from global administrators in every authorization mode. */
const GLOBAL_ADMIN_DENIED_PERMISSIONS: readonly TeamPermission[] = [
  'ach-payment-methods:write',
  'ach-payments:execute',
];

export function deniesGlobalAdmin(
  permissions: readonly TeamPermission[],
  isVrpAdmin: boolean
): boolean {
  return (
    isVrpAdmin &&
    permissions.some((permission) =>
      GLOBAL_ADMIN_DENIED_PERMISSIONS.includes(permission)
    )
  );
}

/**
 * Single source of truth for UI authorization.
 *
 * While the feature is disabled the legacy role flags still govern, so adding
 * a gate is a no-op for teams that have not been migrated. Once enabled, the
 * effective permission array is authoritative on its own: an absent permission
 * denies, and an empty array is a valid "no product access" membership rather
 * than a missing-data signal.
 *
 * VRPlatform administrators follow the same effective-permission and legacy
 * fallback rules as other members, except that the legacy fallback never grants
 * them the ACH permissions the API denies them.
 */
export function resolveTeamPermission({
  permission,
  permissions,
  isUiPermissionRolesEnabled,
  legacy,
  isTeamAdmin,
  isTeamUser,
  isVrpAdmin,
}: ResolveTeamPermissionInput): boolean {
  if (!isUiPermissionRolesEnabled) {
    if (deniesGlobalAdmin([permission], isVrpAdmin)) return false;
    if (legacy === 'teamAdmin') return isTeamAdmin;
    if (legacy === 'teamUser') return isTeamUser;
    if (legacy === 'vrpAdmin') return isVrpAdmin;
    return true;
  }

  return permissions.includes(permission);
}

export type ResolveTeamPermissionSetInput = Omit<
  ResolveTeamPermissionInput,
  'permission'
> & {
  /** Every permission the surface needs before it can load. */
  required: readonly TeamPermission[];
};

/**
 * Resolve a surface that needs more than one permission.
 *
 * A gate must cover every read its view performs, not just the primary one.
 * The Owners page lists contacts but resolves listing names, so exposing it on
 * `contacts:read` alone sends a member without `listings:read` to a page whose
 * combined query fails.
 */
export function resolveTeamPermissionSet({
  required,
  permissions,
  isUiPermissionRolesEnabled,
  legacy,
  isTeamAdmin,
  isTeamUser,
  isVrpAdmin,
}: ResolveTeamPermissionSetInput): boolean {
  if (!isUiPermissionRolesEnabled) {
    if (deniesGlobalAdmin(required, isVrpAdmin)) return false;
    if (legacy === 'teamAdmin') return isTeamAdmin;
    if (legacy === 'teamUser') return isTeamUser;
    if (legacy === 'vrpAdmin') return isVrpAdmin;
    return true;
  }

  return hasEveryTeamPermission(permissions, required);
}

export function hasEveryTeamPermission(
  permissions: readonly TeamPermission[],
  required: readonly TeamPermission[]
): boolean {
  return required.every((permission) => permissions.includes(permission));
}

export function hasSomeTeamPermission(
  permissions: readonly TeamPermission[],
  required: readonly TeamPermission[]
): boolean {
  return required.some((permission) => permissions.includes(permission));
}
